import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Send, CheckCircle2 } from "lucide-react";

import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initialState = {
  name: "",
  email: "",
  organisation: "",
  phone: "",
  enquiry_type: "individual",
  subject: "",
  message: "",
  consent: false,
  website: "",
};

const ENQUIRY_OPTIONS = [
  { value: "individual", label: "Individual learner enquiry" },
  { value: "organisation", label: "Organisation / training provider" },
  { value: "business", label: "Business / partnership" },
  { value: "general", label: "General enquiry" },
];

export default function ContactForm() {
  const [form, setForm] = useState(initialState);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  const update = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.consent) {
      toast.error("Please confirm consent to be contacted.");
      return;
    }
    if (form.message.trim().length < 10) {
      toast.error("Please provide a little more detail in your message.");
      return;
    }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/contact`, form);
      toast.success(res.data?.message || "Enquiry received.");
      setSuccess(true);
      setForm(initialState);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Sorry — something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (success) {
    return (
      <div
        className="rounded-md border border-slate-200 bg-white p-8 text-center"
        data-testid="contact-form-success"
      >
        <div className="mx-auto h-12 w-12 rounded-md bg-safety/10 text-safety flex items-center justify-center">
          <CheckCircle2 className="h-6 w-6" />
        </div>
        <h3 className="mt-4 font-display font-bold text-2xl text-navy">Thank you</h3>
        <p className="mt-2 text-sm text-slate-600 max-w-md mx-auto leading-relaxed">
          Your enquiry has been received. A member of our team will be in touch shortly.
        </p>
        <button
          type="button"
          onClick={() => setSuccess(false)}
          data-testid="contact-form-new-enquiry"
          className="mt-6 inline-flex items-center gap-2 border border-navy text-navy hover:bg-slate-50 font-semibold text-sm px-4 py-2.5 rounded-md transition-colors"
        >
          Submit another enquiry
        </button>
      </div>
    );
  }

  return (
    <form
      onSubmit={handleSubmit}
      className="rounded-md border border-slate-200 bg-white p-6 md:p-8 grid gap-5"
      data-testid="contact-form"
      noValidate
    >
      <div className="grid gap-5 md:grid-cols-2">
        <div className="grid gap-1.5">
          <Label htmlFor="name">Full name <span className="text-safety">*</span></Label>
          <Input
            id="name"
            value={form.name}
            onChange={(e) => update("name", e.target.value)}
            required
            minLength={2}
            data-testid="contact-input-name"
            placeholder="Jane Driver"
          />
        </div>
        <div className="grid gap-1.5">
          <Label htmlFor="email">Email <span className="text-safety">*</span></Label>
          <Input
            id="email"
            type="email"
            value={form.email}
            onChange={(e) => update("email", e.target.value)}
            required
            data-testid="contact-input-email"
            placeholder="you@example.com"
          />
        </div>
        <div className="grid gap-1.5">
          <Label htmlFor="organisation">Organisation</Label>
          <Input
            id="organisation"
            value={form.organisation}
            onChange={(e) => update("organisation", e.target.value)}
            data-testid="contact-input-organisation"
            placeholder="Operator, training provider, heritage railway, etc."
          />
        </div>
        <div className="grid gap-1.5">
          <Label htmlFor="phone">Phone</Label>
          <Input
            id="phone"
            value={form.phone}
            onChange={(e) => update("phone", e.target.value)}
            data-testid="contact-input-phone"
            placeholder="Optional"
          />
        </div>
      </div>

      <div className="grid gap-5 md:grid-cols-2">
        <div className="grid gap-1.5">
          <Label htmlFor="enquiry_type">Enquiry type <span className="text-safety">*</span></Label>
          <Select value={form.enquiry_type} onValueChange={(v) => update("enquiry_type", v)}>
            <SelectTrigger id="enquiry_type" data-testid="contact-select-enquiry-type">
              <SelectValue placeholder="Select an option" />
            </SelectTrigger>
            <SelectContent>
              {ENQUIRY_OPTIONS.map((o) => (
                <SelectItem key={o.value} value={o.value} data-testid={`contact-enquiry-${o.value}`}>
                  {o.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="grid gap-1.5">
          <Label htmlFor="subject">Subject</Label>
          <Input
            id="subject"
            value={form.subject}
            onChange={(e) => update("subject", e.target.value)}
            data-testid="contact-input-subject"
            placeholder="What is your enquiry about?"
          />
        </div>
      </div>

      <div className="grid gap-1.5">
        <Label htmlFor="message">Message <span className="text-safety">*</span></Label>
        <Textarea
          id="message"
          value={form.message}
          onChange={(e) => update("message", e.target.value)}
          required
          minLength={10}
          rows={6}
          data-testid="contact-input-message"
          placeholder="Tell us about your enquiry, training needs, or career goals."
        />
      </div>

      <div className="hidden" aria-hidden="true">
        <Label htmlFor="website">Website</Label>
        <Input
          id="website"
          tabIndex={-1}
          autoComplete="off"
          value={form.website}
          onChange={(e) => update("website", e.target.value)}
        />
      </div>

      <label className="flex items-start gap-3 text-sm text-slate-600">
        <Checkbox
          id="consent"
          checked={form.consent}
          onCheckedChange={(v) => update("consent", Boolean(v))}
          data-testid="contact-input-consent"
          className="mt-0.5"
        />
        <span>
          I consent to UK Train Driver Academy Ltd contacting me about my enquiry. I have read the{" "}
          <a href="/legal/privacy" className="text-navy underline underline-offset-2 hover:text-safety">Privacy Policy</a>.
        </span>
      </label>

      <div className="flex flex-wrap items-center justify-between gap-3 pt-2">
        <p className="text-xs text-slate-500 max-w-sm">
          We aim to respond within two working days. For urgent operational matters, please contact your employer directly.
        </p>
        <button
          type="submit"
          disabled={submitting}
          data-testid="contact-form-submit"
          className="inline-flex items-center gap-2 bg-safety hover:bg-[#e66e00] disabled:opacity-60 disabled:cursor-not-allowed text-white font-semibold px-6 py-3 rounded-md transition-colors"
        >
          {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
          {submitting ? "Sending…" : "Send enquiry"}
        </button>
      </div>
    </form>
  );
}
